/**
 * Generates a unique ID suitable for client-side keys/identifiers (e.g.,
 * React list keys, ephemeral row IDs).
 *
 * NOT for security-sensitive use — fallbacks below are not cryptographically
 * strong.
 *
 * Resolution order:
 * 1. `crypto.randomUUID()` — preferred (secure contexts).
 * 2. `crypto.getRandomValues()` — available in all modern browsers
 *    regardless of secure context. Useful when the page is served over a
 *    non-`localhost` http origin (e.g. `*.local.<domain>`), which Chromium
 *    treats as non-secure and where `randomUUID` is therefore undefined.
 * 3. Timestamp-based fallback — last resort.
 */
export function generateSafeUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const bytes = crypto.getRandomValues(new Uint8Array(8));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  }
  return `${Date.now().toString(36)}-${performance.now().toString(36)}`;
}
